import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fromUntyped } from "@/integrations/supabase/untyped";
import { useAuth } from "@/hooks/useAuth";

/**
 * useWaterTrackingToggle — controla o flag `tracking_agua_ativo` em users_app.
 *
 * Phase 6 do Meal + Water Tracking V1. Source-of-truth fica em users_app
 * (lido direto via Supabase) e a escrita passa pelo backend
 * (PUT /api/profile/water-tracking) por causa da regra de ouro do projeto:
 * mutations sempre via backend.
 *
 * - Read: direto Supabase (.from("users_app")) — barato e auto-RLS.
 * - Write: backend (precisa de Bearer token via supabase.auth.getSession()).
 * - Optimistic: setEnabled atualiza state local imediatamente. Se o backend
 *   responder 4xx/5xx, faz rollback pro valor anterior + propaga o erro.
 *
 * Sprint A (2026-05-21) refactor: o IO-heavy hook agora é `useWaterTrackingState`
 * — uma ÚNICA instância vive dentro de `<WaterTrackingProvider>` nas rotas que
 * exibem água, e HojeView/WaterDropHeaderIcon consomem via context pelo hook
 * público `useWaterTrackingToggle`. Antes cada caller fazia seu próprio
 * Supabase read, gerando flicker entre toggle + render do header.
 */

const API_URL: string =
  (import.meta.env.VITE_API_URL as string) || "https://api.ondiet.app";

export interface UseWaterTrackingToggleResult {
  /** `null` = ainda carregando ou erro inicial; `boolean` = valor conhecido. */
  enabled: boolean | null;
  /** Loading do bootstrap read (não do write — write usa optimistic). */
  loading: boolean;
  /** True enquanto um setEnabled está em flight (UI deve disable o toggle). */
  isPending: boolean;
  /** Última mensagem de erro (read ou write). Reseta no próximo setEnabled. */
  error: string | null;
  /** Optimistic flip. Throws em falha (após rollback). */
  setEnabled: (next: boolean) => Promise<void>;
}

const accountChangedError = (): Error => {
  const error = new Error("Conta alterada durante a atualização");
  error.name = "AbortError";
  return error;
};

const getAuthHeaders = async (expectedAuthId: string): Promise<Record<string, string>> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("Sessão expirada — faça login novamente");
  }
  // A sessão do SDK pode mudar antes de o AuthProvider conseguir renderizar a
  // nova identidade. Nunca permita que uma intenção da conta A saia com o
  // token recém-restaurado da conta B nesse intervalo.
  if (session.user.id !== expectedAuthId) {
    throw accountChangedError();
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.access_token}`,
  };
};

const parseError = async (res: Response, fallback: string): Promise<string> => {
  try {
    const body = (await res.json()) as Record<string, unknown>;
    return (
      (body.detail as string) ||
      (body.message as string) ||
      (body.error as string) ||
      fallback
    );
  } catch {
    return fallback;
  }
};

/**
 * IO-heavy hook. Internal — usado APENAS pelo Provider (single instance) e
 * pelos tests que validam diretamente a lógica de Supabase read + PUT.
 * Componentes da app consomem via `useWaterTrackingToggle()` (context).
 */
export const useWaterTrackingState = (
  knownAuthId?: string | null,
  ioEnabled = true,
): UseWaterTrackingToggleResult => {
  const [enabled, setEnabledState] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(ioEnabled);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const committedRef = useRef<boolean | null>(null);
  const userInteractedRef = useRef<boolean>(false);
  const loadedAuthIdRef = useRef<string | null>(null);
  const [stateAuthId, setStateAuthId] = useState<string | null>(knownAuthId ?? null);
  const identityRef = useRef<string | null | undefined>(knownAuthId);
  const identityGenerationRef = useRef(0);
  if (identityRef.current !== knownAuthId) {
    identityRef.current = knownAuthId;
    identityGenerationRef.current += 1;
    loadedAuthIdRef.current = null;
  }

  useEffect(() => {
    if (!ioEnabled) {
      setLoading(false);
      return;
    }
    if (knownAuthId && loadedAuthIdRef.current === knownAuthId) return;
    let cancelled = false;
    setEnabledState(null);
    setLoading(true);
    setIsPending(false);
    setError(null);
    committedRef.current = null;
    userInteractedRef.current = false;
    setStateAuthId(knownAuthId ?? null);
    const fetchToggle = async () => {
      try {
        let authId = knownAuthId;
        let authMessage: string | null = null;
        // Compatibilidade dos testes unitários e de usos isolados: a árvore
        // real passa o id já restaurado pelo AuthProvider e não paga este GET.
        if (authId === undefined) {
          const {
            data: { user },
            error: authError,
          } = await supabase.auth.getUser();
          authId = user?.id ?? null;
          authMessage = authError?.message ?? null;
        }
        if (!authId) {
          if (!cancelled) {
            setError(authMessage || "Usuário não autenticado");
            setLoading(false);
          }
          return;
        }
        const { data, error: supaError } = await fromUntyped("users_app")
          .select("tracking_agua_ativo")
          .eq("auth_id", authId)
          .maybeSingle();

        if (cancelled) return;

        if (supaError) {
          setError(supaError.message || "Erro ao buscar toggle de água");
          setLoading(false);
          return;
        }
        const raw = data?.tracking_agua_ativo;
        const initial = typeof raw === "boolean" ? raw : false;
        if (!userInteractedRef.current) {
          setEnabledState(initial);
          committedRef.current = initial;
        }
        loadedAuthIdRef.current = authId;
        setStateAuthId(authId);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Erro inesperado";
        setError(msg);
        setLoading(false);
      }
    };
    void fetchToggle();
    return () => {
      cancelled = true;
    };
  }, [knownAuthId, ioEnabled]);

  const setEnabled = useCallback(
    async (next: boolean): Promise<void> => {
      const writeGeneration = identityGenerationRef.current;
      const expectedAuthId = knownAuthId ?? stateAuthId;
      if (!expectedAuthId) {
        throw new Error("Usuário não autenticado");
      }
      userInteractedRef.current = true;
      setEnabledState(next);
      setIsPending(true);
      setError(null);
      try {
        const headers = await getAuthHeaders(expectedAuthId);
        if (writeGeneration !== identityGenerationRef.current) {
          throw accountChangedError();
        }
        const res = await fetch(`${API_URL}/api/profile/water-tracking`, {
          method: "PUT",
          headers,
          body: JSON.stringify({ enabled: next }),
        });
        if (!res.ok) {
          // Throw direto — catch faz rollback UMA vez (review pass 3 cr STRONG #1).
          // Antes a gente chamava setEnabledState(committedRef.current) aqui E no catch,
          // duplicando o update (idempotente, mas confuso em StrictMode).
          const msg = await parseError(
            res,
            `Erro ao atualizar toggle (HTTP ${res.status})`,
          );
          throw new Error(msg);
        }
        if (writeGeneration === identityGenerationRef.current) {
          committedRef.current = next;
        }
      } catch (err) {
        if (writeGeneration !== identityGenerationRef.current) {
          throw err instanceof Error ? err : new Error("Erro inesperado");
        }
        // Único rollback — cobre tanto !res.ok quanto network/auth errors.
        setEnabledState(committedRef.current);
        const msg = err instanceof Error ? err.message : "Erro inesperado";
        setError(msg);
        throw err instanceof Error ? err : new Error(msg);
      } finally {
        if (writeGeneration === identityGenerationRef.current) {
          setIsPending(false);
        }
      }
    },
    [knownAuthId, stateAuthId],
  );

  // useMemo evita criar objeto novo a cada render do Provider — sem isso, todo
  // consumer (WaterDropHeaderIcon no header + HojeView) re-renderiza
  // mesmo quando nenhuma das 4 primitivas mudou. setEnabled já é useCallback
  // estável, então as 4 primitivas são suficientes pra deps.
  const identityMatches = knownAuthId === undefined || stateAuthId === knownAuthId;
  return useMemo(
    () => ({
      enabled: identityMatches ? enabled : null,
      loading: identityMatches ? loading : true,
      isPending: identityMatches ? isPending : false,
      error: identityMatches ? error : null,
      setEnabled,
    }),
    [identityMatches, enabled, loading, isPending, error, setEnabled],
  );
};

const WaterTrackingContext = createContext<UseWaterTrackingToggleResult | null>(
  null,
);

/**
 * WaterTrackingProvider — hospeda 1 única instância do hook IO-heavy.
 * Envolver as rotas que exibem água (depois do AuthProvider). Crash imediato se
 * algum componente chamar `useWaterTrackingToggle()` fora do Provider.
 */
export const WaterTrackingProvider: React.FC<{
  children: React.ReactNode;
  enabled?: boolean;
}> = ({
  children,
  enabled = true,
}) => {
  const { user } = useAuth();
  const value = useWaterTrackingState(user?.id ?? null, enabled);
  // Usamos createElement em vez de JSX pra manter este arquivo como .ts puro
  // (evita renomear pra .tsx + atualizar callers/test paths).
  return React.createElement(
    WaterTrackingContext.Provider,
    { value },
    children,
  );
};

/**
 * Hook público — HojeView / WaterDropHeaderIcon consomem aqui.
 * Backward-compat com chamadas existentes; o provider é quem garante
 * single-source-of-truth + sem flicker.
 */
export const useWaterTrackingToggle = (): UseWaterTrackingToggleResult => {
  const ctx = useContext(WaterTrackingContext);
  if (!ctx) {
    throw new Error(
      "useWaterTrackingToggle deve ser usado dentro de <WaterTrackingProvider>",
    );
  }
  return ctx;
};
